import React, { useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  Chip,
  Tooltip,
  Paper,
} from '@mui/material';
import { InfoOutlined, CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import type { Option, Variant } from '../../types/product';

interface VariantMatrixProps {
  options: Option[];
  variants: Variant[];
  baseSku: string;
  basePrice: number;
  locations: any[];
  onToggleVariant: (attrs: { name: string; value: string }[]) => void;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

const variantKeyFromAttrs = (attrs: { name: string; value: string }[]) =>
  attrs
    .filter(a => a?.name && a?.value)
    .map(a => `${norm(a.name)}=${norm(a.value)}`)
    .sort()
    .join("|");

const makeAttrs = (pairs: { name: string; value: string }[]) =>
  pairs
    .filter(p => p.name && p.value)
    .map(p => ({ name: p.name.trim(), value: p.value.trim() }))
    .sort((a, b) => a.name.localeCompare(b.name));

const VariantMatrix: React.FC<VariantMatrixProps> = ({
  options,
  variants,
  onToggleVariant,
}) => {
  // ✅ FIX: Normalize attributes when building key map - just for lookup
  const variantsByKey = useMemo(() => {
    const map = new Map<string, Variant>();
    for (const v of variants) {
      map.set(variantKeyFromAttrs(makeAttrs(v.attributes)), v);
    }
    return map;
  }, [variants]);

  // Only show matrix for exactly 2 options
  if (options.length !== 2) {
    return null;
  }

  const [optionA, optionB] = options;
  
  if (!optionA?.name || !optionB?.name || !optionA.values.length || !optionB.values.length) {
    return null;
  }

  const totalCombinations = optionA.values.length * optionB.values.length;
  const existingCount = variantsByKey.size;
  const coverage = Math.round((existingCount / totalCombinations) * 100) || 0;

  return (
    <Card 
      sx={{ 
        mb: 4,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Variant Matrix
            </Typography>
            <Chip
              label={`${existingCount} of ${totalCombinations} variants`}
              size="small"
              color={coverage === 100 ? "success" : "default"}
              sx={{ ml: 1 }}
            />
          </Box>
        }
        subheader={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Select which combinations you carry
            </Typography>
            <Tooltip title="Check the boxes for variants you actually stock. Unchecked combinations won't be created.">
              <InfoOutlined sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </Box>
        }
        sx={{
          '& .MuiCardHeader-content': { overflow: 'hidden' },
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      />
      
      <CardContent sx={{ p: 0, overflowX: 'auto' }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Paper elevation={0} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 700, 
                      bgcolor: 'action.hover',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {optionA.name} \ {optionB.name}
                  </TableCell>
                  {optionB.values.map((bVal) => (
                    <TableCell 
                      key={bVal} 
                      align="center" 
                      sx={{ 
                        fontWeight: 700, 
                        bgcolor: 'action.hover',
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                        minWidth: 100,
                      }}
                    >
                      <Chip 
                        label={bVal} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              
              <TableBody>
                {optionA.values.map((aVal, rowIndex) => (
                  <TableRow 
                    key={aVal}
                    sx={{ 
                      '&:hover': { bgcolor: 'action.hover' },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        bgcolor: rowIndex % 2 === 0 ? 'action.hover' : 'inherit',
                      }}
                    >
                      {aVal}
                    </TableCell>

                    {optionB.values.map((bVal) => {
                      const attrs = [
                        { name: optionA.name, value: aVal },
                        { name: optionB.name, value: bVal },
                      ];
                      const key = variantKeyFromAttrs(makeAttrs(attrs));
                      const exists = variantsByKey.has(key);

                      return (
                        <TableCell 
                          key={bVal} 
                          align="center"
                          sx={{ 
                            bgcolor: rowIndex % 2 === 0 ? 'action.hover' : 'inherit',
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <Checkbox
                            checked={exists}
                            onChange={() => onToggleVariant(attrs)}
                            icon={<CheckBoxOutlineBlank sx={{ color: 'text.disabled' }} />}
                            checkedIcon={<CheckBox sx={{ color: 'primary.main' }} />}
                            sx={{
                              '&:hover': {
                                bgcolor: 'primary.50',
                              },
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>

        <Divider />
        
        <Box sx={{ p: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            💡 Tip: Only check the combos you actually carry (e.g., "Blue" only in "M" and "XL")
          </Typography>
          {coverage < 100 && (
            <Typography variant="caption" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InfoOutlined sx={{ fontSize: 14 }} />
              Save inventory space by excluding unused combinations
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default VariantMatrix;